import { useMemo, useState } from "react";
import { ApartmentRounded, AttachMoneyRounded, CheckCircleRounded, SearchRounded, TimelapseRounded } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion, type Variants } from "framer-motion";
import { colors } from "@components/ui/colors";
import { useSuperAdminCompaniesQuery } from "./hook/useSuperAdminCompaniesQuery";
import { SuperAdminCompany } from "./interfaces";
import { MetricCard, metricsContainerAnimation } from "../components/MetricCard";

type CompanyStatus = "active" | "trial" | "past_due" | "cancelled";
type Order = "asc" | "desc";
const MotionBox = motion(Box);
const MotionPaper = motion(Paper);
const MotionTableRow = motion(TableRow);

const statusStyles: Record<
  CompanyStatus,
  {
    label: string;
    backgroundColor: string;
    color: string;
  }
> = {
  active: {
    label: "Active",
    backgroundColor: "var(--fx-colors-success-100, #D1FAE5)",
    color: "#15803D",
  },
  trial: {
    label: "Trial",
    backgroundColor: "var(--fx-colors-yellow-100, #FFF9C2)",
    color: "#B45309",
  },
  past_due: {
    label: "Past due",
    backgroundColor: "var(--fx-colors-danger-100, #FEE2E2)",
    color: "#DC2626",
  },
  cancelled: {
    label: "Cancelled",
    backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
    color: "var(--fx-surface-textsecondary, #64748B)",
  },
};

const columns = [
  {
    id: "company",
    label: "Company",
    width: 150,
    align: "left",
    sortable: false,
  },
  {
    id: "appPlan",
    label: "App plan",
    width: 100,
    align: "center",
    sortable: false,
  },
  {
    id: "appPlanCost",
    label: "App cost",
    width: 100,
    align: "center",
    sortable: true,
  },
  {
    id: "appStatus",
    label: "App status",
    width: 110,
    align: "center",
    sortable: false,
  },
  {
    id: "appPlanExpiresAt",
    label: "App expires",
    width: 120,
    align: "left",
    sortable: true,
  },
  {
    id: "mobilePlan",
    label: "Mobile",
    width: 60,
    align: "left",
    sortable: false,
  },
  {
    id: "ownerName",
    label: "Owner",
    width: 150,
    align: "center",
    sortable: true,
  },
  {
    id: "ownerEmail",
    label: "Owner email",
    width: 210,
    align: "left",
    sortable: false,
  },
  {
    id: "teamMembers",
    label: "Team members",
    width: 110,
    align: "center",
    sortable: true,
  },
  {
    id: "createdAt",
    label: "Created",
    width: 130,
    align: "center",
    sortable: true,
  },
] as const;

const pageAnimation: Variants = {
  hidden: {
    opacity: 0,
    y: 8,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

const filtersAnimation: Variants = {
  hidden: {
    opacity: 0,
    y: 8,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.12,
      duration: 0.28,
      ease: "easeOut",
    },
  },
};

const tableAnimation: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.16,
      duration: 0.32,
      ease: "easeOut",
    },
  },
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const getPlanExpiration = (value?: string | null) => {
  if (!value) {
    return {
      label: "No active plan",
      color: "var(--fx-surface-texttertiary, #94A3B8)",
    };
  }

  const now = new Date();
  const expirationDate = new Date(value);
  const millisecondsPerDay = 1000 * 60 * 60 * 24;

  const daysRemaining = Math.ceil((expirationDate.getTime() - now.getTime()) / millisecondsPerDay);

  if (daysRemaining < 0) {
    const expiredDays = Math.abs(daysRemaining);

    return {
      label: `Expired ${expiredDays} ${expiredDays === 1 ? "day" : "days"} ago`,
      color: "#DC2626",
    };
  }

  if (daysRemaining === 0) {
    return {
      label: "Expires today",
      color: "#DC2626",
    };
  }

  if (daysRemaining <= 7) {
    return {
      label: `${daysRemaining} ${daysRemaining === 1 ? "day" : "days"} remaining`,
      color: "#DC2626",
    };
  }

  if (daysRemaining <= 30) {
    return {
      label: `${daysRemaining} days remaining`,
      color: "#D97706",
    };
  }

  return {
    label: `${daysRemaining} days remaining`,
    color: "var(--fx-surface-textsecondary, #64748B)",
  };
};

const getCompanyInitials = (companyName: string) => {
  return companyName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

export default function CompaniesView() {
  const { data: companies = [], isLoading } = useSuperAdminCompaniesQuery();

  const theme = useTheme();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<CompanyStatus | "all">("all");
  const [planFilter, setPlanFilter] = useState<string>("all");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(6);

  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof SuperAdminCompany>("createdAt");

  const metrics = useMemo(() => {
    const totalCompanies = companies.length;

    const activeCompanies = companies.filter((company) => company.appStatus === "active" || company.mobileStatus === "active").length;

    const trialCompanies = companies.filter((company) => company.appStatus === "trial").length;

    const monthlyRevenue = companies.reduce((total, company) => {
      const appRevenue = company.appStatus === "active" ? company.appPlanCost : 0;

      const mobileRevenue = company.mobileStatus === "active" ? company.mobilePlanCost : 0;

      return total + appRevenue + mobileRevenue;
    }, 0);

    return {
      totalCompanies,
      activeCompanies,
      trialCompanies,
      monthlyRevenue,
    };
  }, [companies]);

  const availablePlans = useMemo(() => {
    return Array.from(new Set(companies.map((company) => company.appPlan).filter((plan) => plan && plan !== "No plan"))).sort((planA, planB) =>
      planA.localeCompare(planB),
    );
  }, [companies]);

  const filteredCompanies = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return companies.filter((company) => {
      const matchesSearch =
        !normalizedSearch ||
        [company.name, company.id, company.appPlan, company.mobilePlan, company.ownerName, company.ownerEmail]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch);

      const matchesStatus = statusFilter === "all" || company.appStatus === statusFilter;

      const matchesPlan = planFilter === "all" || company.appPlan === planFilter;

      return matchesSearch && matchesStatus && matchesPlan;
    });
  }, [companies, search, statusFilter, planFilter]);

  const sortedCompanies = useMemo(() => {
    return [...filteredCompanies].sort((companyA, companyB) => {
      const valueA = companyA[orderBy];
      const valueB = companyB[orderBy];

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return 1;
      if (valueB == null) return -1;

      if (typeof valueA === "number" && typeof valueB === "number") {
        return order === "asc" ? valueA - valueB : valueB - valueA;
      }

      const stringA = String(valueA);
      const stringB = String(valueB);

      return order === "asc" ? stringA.localeCompare(stringB) : stringB.localeCompare(stringA);
    });
  }, [filteredCompanies, order, orderBy]);

  const visibleCompanies = useMemo(() => {
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;

    return sortedCompanies.slice(startIndex, endIndex);
  }, [sortedCompanies, page, pageSize]);

  const handleSort = (column: keyof SuperAdminCompany) => {
    const isSameColumn = orderBy === column;

    setOrder(isSameColumn && order === "asc" ? "desc" : "asc");
    setOrderBy(column);
  };

  const resetPage = () => {
    setPage(0);
  };

  return (
    <MotionBox
      initial="hidden"
      animate="visible"
      variants={pageAnimation}
      sx={{
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",

        px: {
          xs: 1.5,
          sm: 2,
        },

        pt: {
          xs: 1.5,
          sm: 2,
        },

        mb: 0,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        <MotionBox variants={metricsContainerAnimation} initial="hidden" animate="visible" sx={{ mb: 1 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                label="Total companies"
                value={isLoading ? <CircularProgress size={20} thickness={5} sx={{ color: colors.orange.base }} /> : String(metrics.totalCompanies)}
                helper={isLoading ? "" : "Registered companies"}
                icon={<ApartmentRounded sx={{ color: colors.orange.base }} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                label="Active companies"
                value={isLoading ? <CircularProgress size={20} thickness={5} sx={{ color: colors.orange.base }} /> : String(metrics.activeCompanies)}
                helper={
                  isLoading
                    ? ""
                    : `${metrics.totalCompanies > 0 ? Math.round((metrics.activeCompanies / metrics.totalCompanies) * 100) : 0}% of total`
                }
                icon={<CheckCircleRounded sx={{ color: colors.orange.base }} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                label="Monthly revenue"
                value={
                  isLoading ? (
                    <CircularProgress size={20} thickness={5} sx={{ color: colors.orange.base }} />
                  ) : (
                    `$${metrics.monthlyRevenue.toLocaleString("en-US")}`
                  )
                }
                helper={isLoading ? "" : "Recurring plan revenue"}
                icon={<AttachMoneyRounded sx={{ color: colors.orange.base }} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                label="Trial companies"
                value={isLoading ? <CircularProgress size={20} thickness={5} sx={{ color: colors.orange.base }} /> : String(metrics.trialCompanies)}
                helper={isLoading ? "" : "Currently evaluating"}
                icon={<TimelapseRounded sx={{ color: colors.orange.base }} />}
              />
            </Grid>
          </Grid>
        </MotionBox>

        <MotionBox variants={filtersAnimation} initial="hidden" animate="visible">
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Typography
              sx={{
                color: "text.primary",
                fontSize: 15,
                fontWeight: 800,
              }}
            >
              Company directory
            </Typography>

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              flexWrap="wrap"
              gap={1}
              alignItems={{
                xs: "stretch",
                sm: "center",
              }}
            >
              <TextField
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  resetPage();
                }}
                placeholder="Search company or owner..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded
                        sx={{
                          color: colors.orange.base,
                          fontSize: 17,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: {
                    xs: "100%",
                    sm: 280,
                  },
                  "& .MuiOutlinedInput-root": {
                    minHeight: 36,
                    borderRadius: "16px",
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    fontSize: "0.8125rem",
                    transition: "all 150ms ease",
                    "& fieldset": {
                      borderColor: "var(--fx-surface-border, #E2E8F0)",
                    },
                    "&:hover fieldset": {
                      borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                    },
                    "&.Mui-focused": {
                      transform: "translateY(-1px)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#F28C28",
                    },
                  },
                }}
              />

              <Select
                size="small"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value as CompanyStatus | "all");
                  resetPage();
                }}
                sx={{
                  height: 36,
                  minWidth: 140,
                  borderRadius: "16px",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  fontSize: "0.8125rem",
                  transition: "transform 150ms ease",

                  "&:hover": {
                    transform: "translateY(-1px)",
                  },

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.gray[100],
                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.orange.base,
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.orange.base,
                    borderWidth: 1,
                  },
                }}
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="trial">Trial</MenuItem>
                <MenuItem value="past_due">Past due</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>

              <Select
                size="small"
                value={planFilter}
                onChange={(event) => {
                  setPlanFilter(event.target.value);
                  resetPage();
                }}
                sx={{
                  height: 36,
                  minWidth: 140,
                  borderRadius: "16px",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  fontSize: "0.8125rem",
                  transition: "transform 150ms ease",

                  "&:hover": {
                    transform: "translateY(-1px)",
                  },

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.gray[100],
                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.orange.base,
                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.orange.base,
                    borderWidth: 1,
                  },
                }}
              >
                <MenuItem value="all">All plans</MenuItem>

                {availablePlans.map((plan) => (
                  <MenuItem key={plan} value={plan}>
                    {plan}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>
        </MotionBox>

        <MotionPaper
          elevation={0}
          variants={tableAnimation}
          initial="hidden"
          animate="visible"
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            bgcolor: theme.palette.grey[50],
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <TableContainer
            sx={{
              minHeight: 320,
              maxHeight: {
                xs: 460,
                md: 520,
              },
              overflow: "auto",
            }}
          >
            {isLoading && companies.length === 0 ? (
              <Box
                sx={{
                  height: 360,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <CircularProgress
                  size={28}
                  thickness={5}
                  sx={{
                    color: "#F28C28",
                  }}
                />
              </Box>
            ) : (
              <Table
                stickyHeader
                size="small"
                sx={{
                  tableLayout: "fixed",
                  minWidth: 1350,
                }}
              >
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          width: column.width,
                          minWidth: column.width,
                          maxWidth: column.width,
                          bgcolor: "var(--fx-surface-base, #FFFFFF)",
                          fontSize: "0.6875rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          color: "text.secondary",
                          py: 1.5,
                          borderBottom: `1px solid ${theme.palette.grey[200]}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : "asc"}
                            onClick={() => handleSort(column.id as keyof SuperAdminCompany)}
                            sx={{
                              "&.MuiTableSortLabel-active": {
                                color: "#F28C28",
                              },
                              "& .MuiTableSortLabel-icon": {
                                ml: 0.5,
                                color: "#F28C28 !important",
                              },
                            }}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleCompanies.length > 0 ? (
                    <AnimatePresence mode="popLayout">
                      {visibleCompanies.map((company, index) => {
                        const appStatusStyle = statusStyles[company.appStatus];
                        const appExpiration = getPlanExpiration(company.appPlanExpiresAt);

                        const hasMobilePlan = company.mobilePlan !== "No mobile plan";
                        return (
                          <MotionTableRow
                            key={company.id}
                            layout
                            initial={{
                              opacity: 0,
                              y: 6,
                            }}
                            animate={{
                              opacity: 1,
                              y: 0,
                              transition: {
                                delay: index * 0.035,
                                duration: 0.2,
                                ease: "easeOut",
                              },
                            }}
                            exit={{
                              opacity: 0,
                              x: -8,
                              transition: {
                                duration: 0.15,
                              },
                            }}
                            whileHover={{
                              backgroundColor: theme.palette.grey[50],
                              transition: {
                                duration: 0.12,
                              },
                            }}
                            whileTap={{
                              scale: 0.998,
                            }}
                            onClick={() => {
                              console.log("Selected company:", company);
                            }}
                            sx={{
                              cursor: "pointer",
                              bgcolor: "var(--fx-surface-base, #FFFFFF)",
                            }}
                          >
                            <TableCell
                              sx={{
                                width: 150,
                                minWidth: 150,
                                maxWidth: 150,
                                py: 0.75,
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <MotionBox
                                  whileHover={{
                                    scale: 1.08,
                                    rotate: 2,
                                  }}
                                  transition={{
                                    duration: 0.15,
                                  }}
                                  sx={{
                                    flexShrink: 0,
                                  }}
                                >
                                  <Avatar
                                    src={company.avatar || undefined}
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      bgcolor: "var(--fx-colors-warning-50, #FFF7ED)",
                                      color: "#C2410C",
                                      fontSize: 10,
                                      fontWeight: 800,
                                    }}
                                  >
                                    {getCompanyInitials(company.name)}
                                  </Avatar>
                                </MotionBox>

                                <Box sx={{ minWidth: 0 }}>
                                  <Tooltip title={company.name} placement="top" arrow enterDelay={500}>
                                    <Typography
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.8125rem",
                                        color: "text.primary",
                                        lineHeight: 1.25,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        cursor: "default",
                                      }}
                                    >
                                      {company.name}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </Stack>
                            </TableCell>

                            <TableCell align="center" sx={{ py: 0.75 }}>
                              <Chip
                                label={company.appPlan}
                                size="small"
                                sx={{
                                  maxWidth: 100,
                                  height: 22,
                                  fontSize: "0.65rem",
                                  fontWeight: 700,
                                  bgcolor: "var(--fx-surface-muted, #F1F5F9)",
                                  color: "var(--fx-surface-textsecondary, #64748B)",
                                  "& .MuiChip-label": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  },
                                }}
                              />
                            </TableCell>

                            <TableCell
                              align="center"
                              sx={{
                                py: 0.75,
                                fontSize: "0.8125rem",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                              }}
                            >
                              ${company.appPlanCost.toFixed(2)}
                            </TableCell>

                            <TableCell align="center" sx={{ py: 0.75 }}>
                              <Chip
                                label={appStatusStyle.label}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: "0.65rem",
                                  fontWeight: 700,
                                  bgcolor: appStatusStyle.backgroundColor,
                                  color: appStatusStyle.color,
                                  border: "none",
                                }}
                              />
                            </TableCell>

                            <TableCell sx={{ py: 0.75 }}>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "text.primary",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatDate(company.appPlanExpiresAt)}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 0.1,
                                  color: appExpiration.color,
                                  fontWeight: 700,
                                  fontSize: "0.65rem",
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {appExpiration.label}
                              </Typography>
                            </TableCell>

                            <TableCell align="center" sx={{ py: 0.75 }}>
                              {hasMobilePlan ? (
                                <CheckCircleRounded
                                  titleAccess={`${company.mobilePlan} - ${statusStyles[company.mobileStatus].label}`}
                                  sx={{
                                    fontSize: 20,
                                    color: "#15803D",
                                  }}
                                />
                              ) : (
                                <Box
                                  component="span"
                                  title="No mobile plan"
                                  sx={{
                                    display: "inline-grid",
                                    placeItems: "center",
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    bgcolor: "var(--fx-surface-muted, #F1F5F9)",
                                    color: "var(--fx-surface-textsecondary, #64748B)",
                                    fontSize: 14,
                                    fontWeight: 800,
                                    lineHeight: 1,
                                  }}
                                >
                                  ×
                                </Box>
                              )}
                            </TableCell>

                            <TableCell
                              sx={{
                                py: 0.75,
                                fontWeight: 600,
                                fontSize: "0.8125rem",
                                color: "text.primary",
                                overflow: "hidden",
                              }}
                            >
                              <Tooltip title={company.ownerName || ""} placement="top" arrow enterDelay={500}>
                                <Typography
                                  component="span"
                                  sx={{
                                    display: "block",
                                    fontWeight: 600,
                                    fontSize: "0.8125rem",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    cursor: "default",
                                  }}
                                >
                                  {company.ownerName || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell
                              sx={{
                                py: 0.75,
                                overflow: "hidden",
                              }}
                            >
                              <Tooltip title={company.ownerEmail || ""} placement="top" arrow enterDelay={500}>
                                <Typography
                                  component="span"
                                  sx={{
                                    display: "block",
                                    fontSize: "0.75rem",
                                    color: "text.secondary",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    cursor: "default",
                                  }}
                                >
                                  {company.ownerEmail || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell
                              align="center"
                              sx={{
                                py: 0.75,
                                fontWeight: 700,
                                fontSize: "0.8125rem",
                              }}
                            >
                              {company.teamMembers}
                            </TableCell>

                            <TableCell
                              align="center"
                              sx={{
                                py: 0.75,
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatDate(company.createdAt)}
                            </TableCell>
                          </MotionTableRow>
                        );
                      })}
                    </AnimatePresence>
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        align="center"
                        sx={{
                          border: "none",
                          py: 10,
                        }}
                      >
                        <MotionBox
                          initial={{
                            opacity: 0,
                            y: 5,
                          }}
                          animate={{
                            opacity: 1,
                            y: 0,
                          }}
                          transition={{
                            duration: 0.22,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 500,
                            }}
                          >
                            No companies match the selected filters
                          </Typography>
                        </MotionBox>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          <TablePagination
            component="div"
            count={sortedCompanies.length}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={(_event, nextPage) => {
              setPage(nextPage);
            }}
            rowsPerPageOptions={[pageSize]}
            labelRowsPerPage=""
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",

              "& .MuiTablePagination-toolbar": {
                minHeight: 38,
              },

              "& .MuiTablePagination-selectLabel": {
                display: "none",
              },

              "& .MuiTablePagination-input": {
                display: "none",
              },

              "& .MuiTablePagination-spacer": {
                flex: 1,
              },

              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.8125rem",
              },

              "& .MuiIconButton-root": {
                transition: "transform 150ms ease, background-color 150ms ease",

                "&:hover": {
                  transform: "scale(1.06)",
                },
              },
            }}
          />
        </MotionPaper>
      </Box>
    </MotionBox>
  );
}
